// Which devices a SCAN may offer (owner, 2026-10-19).
//
//   "A user can only scan a device that is not assigned to anyone. An assigned
//    device can only be SHARED, not scanned."
//
// So a Bluetooth scan (and the old hotspot radar) lists a found board only when
// the SERVER says nobody has it. An assigned board — anyone's, the user's own
// included — never appears in a scan; it is reached from the user's rooms, or
// given to somebody else by QR share (docs/21). A Super Admin on the bench sees
// everything.
//
// ⚠️ "NOT ON THE SERVER" IS NEW, "SERVER UNREACHABLE" IS HIDDEN. A board that
// has never been online may have no DevicesInstalled row yet — that is the
// newest a device can be, so it is offered. But when the lookup FAILS the phone
// cannot prove the board is unowned, and showing it would be guessing in the
// direction that leaks somebody's device. Adding a device needs the internet
// anyway.
//
// ⚠️ THIS IS ENFORCED BY THE APP, NOT THE DEVICE. The firmware has no owner lock
// yet (CLAIM:/OWNER: never built, docs/13) and the BLE key is per family, so a
// modified app can still connect to any board. This removes the everyday path —
// customers seeing and adding other people's devices — not the determined one.

import { getDeviceDetailFresh } from '../api/api';

export const SCAN_NEW = 'new';               // on the server, unassigned
export const SCAN_UNREGISTERED = 'unregistered'; // no record yet — also new
export const SCAN_ASSIGNED = 'assigned';
export const SCAN_UNKNOWN = 'unknown';       // lookup failed — cannot tell

const int = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * The verdict for one device-detail row (GetDeviceDetailbyUniqueNumber).
 * Assigned = an owner (> 0; the factory uses -2), a location, or any user
 * mapping — any one of them means somebody has it.
 */
export function verdictOfRow(row) {
  if (!row) return SCAN_UNREGISTERED;
  const owner = Math.max(int(row.MasterUserId), int(row.DeviceMasterUserId));
  const location = int(row.LocationID ?? row.LocationId);
  const mapping = String(row.DeviceMappingStatus ?? '').trim().toLowerCase();
  const mapped = mapping !== '' && mapping !== 'not mapped';
  return owner > 0 || location > 0 || mapped ? SCAN_ASSIGNED : SCAN_NEW;
}

/** From deviceAdmin.findDevice's `{ found, device }` / `{ error }` shape. */
export function verdictOfLookup(r) {
  if (!r || r.loading) return null;   // still asking
  if (r.error) return SCAN_UNKNOWN;
  if (!r.found) return SCAN_UNREGISTERED;
  return verdictOfRow(r.device);
}

/** May this board be listed in a scan for this user? */
export function visibleInScan(verdict, { isSuperAdmin = false } = {}) {
  if (isSuperAdmin) return true;
  return verdict === SCAN_NEW || verdict === SCAN_UNREGISTERED;
}

/** Ask the server. -> verdict (never throws). */
export async function lookupScanVerdict(uniqueNumber) {
  const uno = String(uniqueNumber || '').trim();
  if (!uno) return SCAN_UNKNOWN;
  try {
    const res = await getDeviceDetailFresh(uno);
    // This backend answers some failures as HTTP 200 + { status: "500" }.
    if (res && res.data && !Array.isArray(res.data)) return SCAN_UNKNOWN;
    const rows = Array.isArray(res && res.data) ? res.data : null;
    if (!rows) return SCAN_UNKNOWN;
    return verdictOfRow(rows[0] || null);
  } catch (e) {
    return SCAN_UNKNOWN;
  }
}
